import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    Upload,
    Eye,
    CheckCircle,
    Warning,
    Clock,
    File,
    Robot,
    FileText,
    Sparkle
} from '@phosphor-icons/react';
import { ExtractionStatusBadge } from '../DocumentExtractor';
import { DocumentRow } from './DocumentRow';

export const RequiredDocumentCard = ({
    docReq,
    uploadedDocs,
    onUpload,
    onDownload,
    onPreview,
    onReplace,
    onDelete,
    onValidate,
    onViewVersions,
    onExtract,
    onVerifyExtraction,
    onRegeneratePdf,
    canValidate,
    nodeStatus
}) => {
    const hasDocument = uploadedDocs.length > 0;
    const latestDoc = uploadedDocs.find(d => d.is_latest) || uploadedDocs[0];
    const isComplete = hasDocument && (latestDoc?.validation_status === 'approvato' || latestDoc?.validation_status === 'pending');
    const isRejected = latestDoc?.validation_status === 'rifiutato';
    const supportsExtraction = docReq.supports_extraction === true;
    const hasExtraction = latestDoc?.extraction_status;

    return (
        <div
            className={`border rounded-lg overflow-hidden ${
                isComplete ? 'border-green-200 bg-green-50/30' :
                isRejected ? 'border-red-200 bg-red-50/30' :
                docReq.mandatory ? 'border-red-200 bg-red-50/30' :
                'border-border'
            }`}
        >
            <div className="p-3 flex items-center justify-between border-b bg-muted/20">
                <div className="flex items-center gap-2">
                    {isComplete ? (
                        <CheckCircle weight="fill" className="w-5 h-5 text-green-600" />
                    ) : isRejected ? (
                        <Warning weight="fill" className="w-5 h-5 text-red-600" />
                    ) : docReq.mandatory ? (
                        <Warning weight="fill" className="w-5 h-5 text-red-600" />
                    ) : (
                        <Clock className="w-5 h-5 text-muted-foreground" />
                    )}
                    <span className="font-medium text-sm">{docReq.label || docReq.code}</span>
                    {docReq.mandatory && (
                        <Badge variant="destructive" className="text-xs">Obbligatorio</Badge>
                    )}
                    {supportsExtraction && (
                        <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-300">
                            <Robot className="w-3 h-3 mr-1" />
                            Estrazione AI
                        </Badge>
                    )}
                </div>
                {nodeStatus === 'in_corso' && (
                    <Button
                        size="sm"
                        variant={hasDocument ? "outline" : "default"}
                        onClick={() => onUpload(docReq)}
                        data-testid={`upload-btn-${docReq.code}`}
                    >
                        <Upload className="w-4 h-4 mr-1" />
                        {hasDocument ? 'Sostituisci' : 'Carica'}
                    </Button>
                )}
            </div>

            {hasDocument ? (
                <div className="p-2 space-y-2">
                    {uploadedDocs.filter(d => d.is_latest).map(doc => (
                        <div key={doc.id} className="space-y-2">
                            <DocumentRow
                                doc={doc}
                                onDownload={onDownload}
                                onPreview={onPreview}
                                onReplace={onReplace}
                                onDelete={onDelete}
                                onValidate={onValidate}
                                onViewVersions={onViewVersions}
                                canValidate={canValidate}
                                canDelete={nodeStatus === 'in_corso'}
                            />

                            {/* Stato estrazione e azioni */}
                            {supportsExtraction && (
                                <div className="ml-11 flex items-center gap-2 flex-wrap">
                                    {hasExtraction && (
                                        <ExtractionStatusBadge status={doc.extraction_status} />
                                    )}

                                    {nodeStatus === 'in_corso' && !hasExtraction && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-violet-700 border-violet-300 hover:bg-violet-50"
                                            onClick={() => onExtract(doc)}
                                        >
                                            <Sparkle className="w-4 h-4 mr-1" />
                                            Estrai Dati
                                        </Button>
                                    )}

                                    {doc.extraction_status === 'estratto' && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => onVerifyExtraction(doc)}
                                        >
                                            <Eye className="w-4 h-4 mr-1" />
                                            Verifica Dati
                                        </Button>
                                    )}

                                    {doc.extraction_status === 'validato' && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-green-700"
                                            onClick={() => onRegeneratePdf(doc)}
                                        >
                                            <FileText className="w-4 h-4 mr-1" />
                                            Genera PDF Compilato
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="p-4 text-center text-muted-foreground">
                    <File className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nessun file caricato</p>
                    {supportsExtraction && (
                        <p className="text-xs text-violet-600 mt-1">
                            Carica il documento per estrarre automaticamente i dati
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};
